/**
 * Shared client-submission financial math (admin dashboard + client ledger).
 * Daewoo-style final payable uses `submissionFinancePreview` (GST/TDS/final).
 */
import { submissionFinancePreview, DEFAULT_PAYMENT_SETTINGS } from './daewooFinance';

export const DEFAULT_FINANCE = { defaultGstPercent: 18, defaultTdsPercent: 2, gstTdsRounding: 'round' };

const num = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

function resolveFinanceOpts(opts) {
    const o = opts && typeof opts === 'object' ? opts : {};
    return {
        defaultGstPercent: num(o.defaultGstPercent ?? DEFAULT_FINANCE.defaultGstPercent) || DEFAULT_FINANCE.defaultGstPercent,
        defaultTdsPercent: num(o.defaultTdsPercent ?? DEFAULT_FINANCE.defaultTdsPercent) || DEFAULT_FINANCE.defaultTdsPercent,
        gstTdsRounding: o.gstTdsRounding === 'truncate2dp' ? 'truncate2dp' : 'round',
    };
}

function resolvePaymentOpts(opts) {
    return { ...DEFAULT_PAYMENT_SETTINGS, ...(opts && typeof opts === 'object' ? opts : {}) };
}

/** Best-effort deal / franchise value from dynamic registration fields. */
export function rowDealAmount(data) {
    if (!data || typeof data !== 'object') return 0;
    const keys = ['totalDealValue', 'dealValue', 'dealAmount', 'budgetINR', 'franchiseFee', 'investmentAmount', 'projectValue'];
    for (const k of keys) {
        const v = data[k];
        if (v != null && v !== '') {
            const n = num(v);
            if (n > 0) return n;
        }
    }
    return 0;
}

/**
 * Per-row financials — aligned with Daewoo `financeCalculator` + registration fields.
 * @param {object} submissionMeta `{ secondPaymentsApprovedSum }` from FormSubmission.secondPayments
 */
export function rowFinancials(data, financeOpts = {}, paymentOpts = {}, submissionMeta = {}) {
    const fin = resolveFinanceOpts(financeOpts);
    const pay = resolvePaymentOpts(paymentOpts);
    const secondApproved = num(submissionMeta.secondPaymentsApprovedSum);
    const prev = submissionFinancePreview(data, fin, pay, { secondPaymentsApprovedSum: secondApproved });
    const { snapshot } = prev;
    const deal = prev.dealAmount;
    const gst = snapshot.gstAmount;
    const tds = snapshot.tdsAmount;
    const payable = snapshot.finalPayableAmount;
    const baseCollected = num(data?.amountCollected ?? data?.collected ?? data?.totalCollected ?? data?.paidAmount);
    const collected = baseCollected + secondApproved;
    const token = prev.tokenReceived;
    const instalments = num(data?.instalments ?? data?.instalmentAmount ?? data?.installmentsPaid ?? data?.emiPaid);
    const refunds = num(data?.refunds ?? data?.refundAmount ?? data?.totalRefunds);
    const paidTowardPayable = prev.totalReceivedTowardPayable + instalments + baseCollected - refunds;

    return {
        deal,
        gst,
        tds,
        payable,
        collected,
        token,
        instalments,
        refunds,
        secondPaymentsApprovedSum: secondApproved,
        paidTowardPayable,
        gstPct: snapshot.gstRate,
        tdsPct: snapshot.tdsRate,
        snapshot,
        loiThreshold: prev.loiThreshold,
        agreementThreshold: prev.agreementThreshold,
        receivedPercent: prev.receivedPercent,
    };
}

/** Aggregate over submissions `{ data }` (or full FormSubmission objects). */
export function aggregateFinancials(items, financeOpts = {}, paymentOpts = {}) {
    const fin = resolveFinanceOpts(financeOpts);
    const pay = resolvePaymentOpts(paymentOpts);
    let deal = 0;
    let gst = 0;
    let tds = 0;
    let payable = 0;
    let collected = 0;
    let token = 0;
    let inst = 0;
    let ref = 0;
    let paidToward = 0;
    for (const item of items) {
        const data = item && typeof item === 'object' && 'data' in item ? (item.data || {}) : {};
        const secondSum = (item.secondPayments || [])
            .filter((p) => p.status === 'approved')
            .reduce((a, p) => a + Number(p.amount || 0), 0);
        const f = rowFinancials(data, fin, pay, { secondPaymentsApprovedSum: secondSum });
        deal += f.deal;
        gst += f.gst;
        tds += f.tds;
        payable += f.payable;
        collected += f.collected;
        token += f.token;
        inst += f.instalments;
        ref += f.refunds;
        paidToward += f.paidTowardPayable;
    }
    const balanceDue = Math.max(0, payable - paidToward);
    const colRate = payable > 0 ? Math.min(100, (paidToward / payable) * 100) : 0;
    const effGst = deal > 0 ? (gst / deal) * 100 : 0;
    const effTds = deal > 0 ? (tds / deal) * 100 : 0;
    const finalNet = deal + gst - tds;
    return {
        deal, gst, tds, payable, collected, token, inst, ref, balanceDue, colRate, effGst, effTds, finalNet,
    };
}

export { DEFAULT_PAYMENT_SETTINGS };
